import React from 'react';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import SwipeIcon from '../../../assets/images/swipe-icon.svg';
import MapSearchIcon from '../../../assets/images/map-search-icon.svg';
import PurchaseLinkIcon from '../../../assets/images/buy-bandwidth.svg';
import PriceBox from './PriceBox';
import { getDateInMediumFormat, getTimeInMediumFormat } from '../../../utils/methods';
import { YearDaysForCalculation, getPortChargePerMonth } from '../../../utils/appConstants';
import { AuthRoutes } from '../../../enums';

function makeStateArray(bandwidth: any): string[] {
  const arr: string[] = [];
  if (bandwidth?.service_description) { arr.push(bandwidth?.service_description); }
  if (bandwidth?.original_owner) { arr.push(bandwidth?.original_owner); }
  if (bandwidth?.on_contract_price) { arr.push(bandwidth?.on_contract_price); }
  if (bandwidth?.on_demand_price) { arr.push(bandwidth?.on_demand_price); }
  return arr;
}

interface BandwidthDetailsProps {
  bandwidth: any;
  onDrawerContentChange: (content: 'list' | 'details') => void;
  isDirect?: boolean;
}

const BandwidthDetails: React.FC<BandwidthDetailsProps> = ({ bandwidth, onDrawerContentChange, isDirect }) => {
  const navigate = useNavigate();
  const consumed = !!(bandwidth?.cc_status === 'consumed');
  const available = !!(bandwidth?.cc_status === 'available');
  const static_bd = !!(bandwidth?.cc_status === 'static');
  const demand = !!(bandwidth?.cc_status === 'demand');
  const contract = !!(bandwidth?.cc_status === 'contract');
  const other = !!(bandwidth?.cc_status === 'other');
  // const onContractPerMonth = bandwidth?.on_contract_price ? (parseFloat(bandwidth.on_contract_price) + (2 * PURCHASE_PORT_CHARGE_PER_MONTH)).toFixed(2) : 0;
  // const onDemandPerMonth = bandwidth?.on_demand_price ? ((parseFloat(bandwidth.on_demand_price) / 100) * ((60 * 24 * YearDaysForCalculation) / 12) + (2 * PURCHASE_PORT_CHARGE_PER_MONTH)).toFixed(2) : 0;
  const ownedByMyOrganization = Number(localStorage.getItem('org_id')) === Number(bandwidth?.organization_id);
  let purchased = false;
  let id = '';
  let labelForId = '';
  let label = '';
  const neText = bandwidth?.near_network_element;
  const feText = bandwidth?.far_network_element;
  const date = `${getDateInMediumFormat(bandwidth?.date)} ${getTimeInMediumFormat(bandwidth?.date)}`;
  if (consumed) {
    id = 'consumed';
    labelForId = 'Consumed';
    if (bandwidth?.color === 'green-fill') {
      label = 'Cross-Connected';
      if (bandwidth?.transaction_type) {
        purchased = true;
      }
    } else {
      label = 'Listed in the Marketplace';
    }
  } else if (available) {
    id = 'available';
    labelForId = 'Available';
    label = 'Bandwidth ';
    if (bandwidth?.transaction_type) {
      label = 'Purchased Bandwidth';
      purchased = true;
    }
  } else if (static_bd) {
    id = 'static';
    labelForId = 'Static';
    label = 'Static Bandwidth';
    if (bandwidth?.transaction_type) {
      purchased = true;
      label = 'Sold Bandwidth';
    }
  } else if (demand) {
    id = 'demand';
    labelForId = 'On-Demand';
    label = 'Marketplace Bandwidth';
  } else if (contract) {
    id = 'contract';
    labelForId = 'On-Contract';
    label = 'Marketplace Bandwidth';
  } else if (other) {
    id = 'other';
    labelForId = 'Unavailable';
    label = 'Bandwidth';
  }

  let insideChild;

  if (available || consumed || static_bd) {
    insideChild = (
      <div className="form-details">
        <div className="subtitle">Bandwidth Details</div>
        <div className="description">
          <label>
            Link Description
          </label>
          <div className="description-value">
            <span>{bandwidth?.description || '-'}</span>
            <span>{bandwidth?.bandwidth_name || ''}</span>
          </div>
        </div>

        <div className="port-names">
          <div className="port-name">
            <label>Port Name</label>
            <div className="port-name-value">
              <span>{bandwidth?.near_port_description}</span>
              <span>{bandwidth?.near_port_name}</span>
            </div>
          </div>
          <div className="port-name">
            <label>Port Name</label>
            <div className="port-name-value">
              <span>{bandwidth?.far_port_description}</span>
              <span>{bandwidth?.far_port_name}</span>
            </div>
          </div>
        </div>

        {/* <div className="port-names">
          <div className="port-name">
            <label>Description</label>
            <div className="desc-value">
              <span>{bandwidth?.near_port_description}</span>
            </div>
          </div>
          <div className="port-name">
            <label>Description</label>
            <div className="desc-value">
              <span>{bandwidth?.far_port_description}</span>
            </div>
          </div>
        </div> */}

        {
          (consumed && bandwidth?.service_description) ? (
            <>
              <div>
                <div className="subtitle">Listing Details</div>
                <div className="description">
                  <label>Service Description</label>
                  <div className="description-value">
                    <span>{bandwidth?.service_description}</span>
                  </div>
                </div>
              </div>
              <div className="purchase-details">
                <PriceBox
                  ownedByMyOrganization={ownedByMyOrganization}
                  organizationName={bandwidth?.original_owner}
                  purchased={purchased}
                  onContractPrice={bandwidth?.on_contract_price && (bandwidth.on_contract_price + (getPortChargePerMonth(bandwidth) * 2))}
                  onDemandPrice={bandwidth?.on_demand_price}
                />
              </div>
            </>
          ) : ''
        }

        {
          (consumed && bandwidth?.service_description) ? (
            <div className="description date">
              <label>Date & Time</label>
              <div className="date-value">
                {date}
              </div>
            </div>
          ) : ''
        }

        {
          (static_bd && purchased) ? (
            <div>
              <div className="subtitle">Transaction Details</div>
              <div className="description">
                <label>Service Description</label>
                <div className="description-value">
                  <span>{bandwidth?.service_description}</span>
                </div>
              </div>
              <div className="purchase-details">
                <PriceBox
                  className="border-bottom"
                  onContractPrice={bandwidth?.price_type === 'on_contract' && bandwidth?.price}
                  onDemandPrice={bandwidth?.price_type === 'on_demand' && bandwidth?.price}
                  date={date}
                  ownedByMyOrganization={ownedByMyOrganization}
                  organizationName={bandwidth?.original_owner}
                  purchased={purchased}
                />
              </div>
            </div>

          ) : ''
        }

      </div>
    );
  }
  if (contract || demand) {
    insideChild = (
      <div className="form-details">
        <div className="subtitle">Listing Details</div>
        <div className="description">
          <label>
            Service Description
          </label>
          <div className="description-value">
            <span>{bandwidth?.service_description || '-'}</span>
          </div>
        </div>

        {
          (bandwidth?.on_contract_price) ? (
            <div className="purchase-details">
              <PriceBox
                ownedByMyOrganization={ownedByMyOrganization}
                organizationName={bandwidth?.original_owner}
                purchased={purchased}
                onContractPrice={bandwidth?.on_contract_price && (bandwidth.on_contract_price + (getPortChargePerMonth(bandwidth) * 2))}
              />
            </div>
          ) : ''
        }

        {
          (bandwidth?.on_demand_price) ? (
            <div className="purchase-details">
              <PriceBox
                ownedByMyOrganization={ownedByMyOrganization}
                organizationName={bandwidth?.original_owner}
                purchased={purchased}
                onDemandPrice={bandwidth?.on_demand_price}
              />
            </div>
          ) : ''
        }
        <div className="links">
          <ul>
            <li>
              <img onClick={() => navigate(AuthRoutes.marketPlaceBandwidth, { state: makeStateArray(bandwidth) })} src={PurchaseLinkIcon} alt="Manage Port Links" />
              <label onClick={() => navigate(AuthRoutes.marketPlaceBandwidth, { state: makeStateArray(bandwidth) })}>Link to purchase this bandwidth</label>
            </li>
          </ul>

        </div>
      </div>

    );
  }
  return (
    <div className="bandwidth-details-form">
      <div className="title">
        {!isDirect && <ArrowBackIcon onClick={() => onDrawerContentChange('list')} />}
        <label>Link Details</label>
      </div>

      <div className="title-info">
        <div className="left">
          <div className={clsx('dot', id)} />
          <label className={clsx(id)}>{labelForId}</label>
        </div>
        <div className="right">
          <label>{label}</label>
        </div>
      </div>

      <div className="bandwidth-points">
        <div className="fe-text">
          <img src={MapSearchIcon} alt="Swipe" data-testid="swipe-icon" />
          {neText}
        </div>
        <div className="swap">
          <img src={SwipeIcon} alt="Swipe" data-testid="swipe-icon" />
        </div>
        <div className="ne-text">
          {feText}
        </div>
      </div>

      {insideChild || (
        <div>
          <br />
          <label>Not for sale</label>
        </div>
      )}
    </div>

  );
};

export default BandwidthDetails;
